import { LDateTime } from '@app/components/date/LDateTime';
import { ListViewChildProps } from '@app/core/list-view/ListView';
import { auditReports } from '@mi-front/api-clients';
import { MrtTable } from '@mi-front/ui';
import { Box, Chip } from '@mui/material';
import { useIntl } from 'react-intl';
import {
  activeStatusMessages,
  aodReportTypeMessages,
  aodRulesMessages,
  triggerMessages,
} from '../../messages';
import { aodRoutes } from '../../routing/routes';

/**
 * Props for the AOD Rules List V2 table component. Extends ListViewChildProps with AOD rule data type.
 */
export type AodRulesListProps = ListViewChildProps<auditReports.AodRule>;

/**
 * Table component for AOD Rules List V2.
 * Displays rules in a table without column filters (filters are in the filter bar).
 */
export function AodRulesList(props: AodRulesListProps) {
  const { $t } = useIntl();
  const {
    data,
    total,
    pagination,
    onPaginationChange,
    sorting,
    onSortingChange,
    isLoading = false,
    isFetching = false,
    showProgress = true,
  } = props;

  return (
    <Box>
      <MrtTable<auditReports.AodRule>
        data={data}
        rowCount={total}
        state={{
          pagination,
          sorting,
          isLoading,
          showProgressBars: showProgress && (isLoading || isFetching),
        }}
        onPaginationChange={onPaginationChange}
        onSortingChange={onSortingChange}
        manualPagination
        manualSorting
        enableColumnFilters={false}
        enableToolbarInternalActions={false}
        paginationDisplayMode="default"
        navigateToOnClick={(id) => aodRoutes.ruleView.gen(id)}
        // TODO: refactor
        muiTopToolbarProps={{
          sx: {
            height: '4px !important',
            minHeight: '4px !important',
          },
        }}
        columns={[
          {
            id: 'createdAt',
            header: $t(aodRulesMessages.createdAt),
            accessorFn: (row) => row.createdAt,
            Cell: ({ row: { original } }) => <LDateTime value={original.createdAt} showRelative />,
            size: 100,
            enableSorting: true,
          },
          {
            id: 'name',
            header: $t(aodRulesMessages.name),
            accessorFn: (row) => row.name,
            Cell: ({ row: { original } }) => <div>{original.name}</div>,
            size: 160,
            enableSorting: true,
          },
          {
            id: 'reportType',
            header: $t(aodRulesMessages.reportType),
            accessorFn: (row) => row.reportType,
            Cell: ({ row: { original } }) => (
              <Chip color="info" label={$t(aodReportTypeMessages[original.reportType])} />
            ),
            size: 120,
          },
          {
            id: 'trigger',
            header: $t(aodRulesMessages.trigger),
            accessorFn: (row) => row.trigger,
            Cell: ({ row: { original } }) => (
              <Chip
                label={$t(
                  triggerMessages[original.trigger] ?? {
                    id: `audit.aod.rules.trigger.${original.trigger}`,
                    defaultMessage: original.trigger,
                  },
                )}
              />
            ),
            size: 160,
          },
          {
            id: 'active',
            header: $t(aodRulesMessages.active),
            accessorFn: (row) => row.active,
            Cell: ({ row: { original } }) => (
              <Chip
                label={
                  original.active
                    ? $t(activeStatusMessages.active)
                    : $t(activeStatusMessages.inactive)
                }
                color={original.active ? 'success' : 'warning'}
              />
            ),
            size: 80,
          },
        ]}
      />
    </Box>
  );
}
